import styled from "styled-components";
// @ts-ignore
import Link from "next/link";
import AddToWishlist from "../wishlist/AddToWishlist";
import AddToCart from "../cart/addToCart";
import UpdateProductQuantity from "./UpdateProductQuantity";
import Badge from "../Badge";
import Image from "next/legacy/image";
import {getNavPath} from "../../shared/navigateUtil";
import {useRouter} from "next/router";
import {IMiniProduct} from "../../interfaces/IMiniProduct";
import parse from 'html-react-parser';
import {getTheme} from "../../shared/themeUtils";
import React, {useContext} from "react";
import {ClientContext} from "../../contexts/ClientContext";
import {ProductSetupOption} from "../ProductSetupOption";
import {ICartItem} from "../../interfaces/ICartItem";
import {getMappedStockColor} from "../../shared/stockColorMapUtil";
import {useSetupOptions} from "../../hooks/useSetupOptions";

const Entry = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${getTheme("colors.border")};
  display: flex;
  flex-direction: row;
  color: ${getTheme("colors.primary")};
  font-size: .9rem;
  
  .left-side {
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-right: ${getTheme("space.0")};
    .prod-image {
      width: 100px;
      display: flex;
      justify-content: center;
      img {
        max-width: 80px;
        max-height: 80px;
      }
    }
  }
  
  .right-side {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .title {
      cursor: pointer;
    }

      .prod-id {
          font-weight: normal;
          font-size: ${getTheme("fontSizes.xSmall")};
      }

      .info {
      display: flex;
      justify-content: space-between;
      width: 100%;
      
      .stock {
        display: inline-block;
        margin: ${getTheme("space.0")} 0;
      }
      
      .price {
        cursor: pointer;
        color: ${getTheme("colors.primary")};
        font-weight: 600;
        font-size: .9rem;
        display: flex;
        flex-direction: column;
        
        .unit-price {
          font-weight: normal;
          color: ${getTheme("colors.secondaryFont")};
          font-size: .7rem;
        }
      }
    }
    .actions {
      display: flex;
      align-items: center;
      .quantity {
        position: relative;
        right: ${getTheme("space.1")};
      }
      button {
        position: relative;
        bottom: 3px;
      }
    }
  }
`

type MiniCartProductProps = {
  product: IMiniProduct,
  wishlistId?: number,
  quantity: number,
  type: "cart" | "wishlist" | "cartWithWishlist" | "wishlistWithCart"
  showSetupOptions?: boolean;
  shoppingCart?: ICartItem[]
}

const MiniCartProduct = (props: MiniCartProductProps) => {
  const {product, quantity, type, showSetupOptions = false} = props;
  const router = useRouter()
  const path = product && product.slug ? product.slug.replace(" ", "-") : "";
  const {clientCtx} = useContext(ClientContext)
  const productSetupOptionsQuery = useSetupOptions(product.productID, product.categoryId, showSetupOptions)

  if (product && product.productName) {
    return (
      <Entry>
        <Link href={getNavPath(router, `p/${path}`)}>
          <div className="left-side">
            <div className="prod-image">
              <Image src={product.image.imageUrl} alt={product.productName} width={"150"} height={"150"}
                     objectFit="contain"/>
            </div>
          </div>
        </Link>

        <div className="right-side">
          <Link href={getNavPath(router, `p/${path}`)}>
            <div className="title">
              <b>{product.brand} </b>{product.productName}

              <div className={"prod-id text-muted"}>#{product.productID}</div>
            </div>
          </Link>
          <div className="info">
            <Link href={getNavPath(router, `p/${path}`)}>
              <div>
                {product.stockDisplayText &&
                  <span className="stock">
                    <Badge text={parse(product.stockDisplayText)} type={getMappedStockColor(product.stockDisplayColor)}
                           size="small" background={false}/>
                  </span>
                }

                {product.unitPrice && !product.isPriceOnDemand &&
                  <div className="price">
                    {product.currency} {(product.price).toFixed(2)}
                    <span className="unit-price">
                      {props.quantity} x {product.currency} {product.unitPrice.toFixed(2)}
                    </span>
                  </div>
                }

                {!product.unitPrice && !product.isPriceOnDemand &&
                  <div className="price">
                    {product.currency} {(props.quantity * product.price).toFixed(2)}
                    <span className="unit-price">
                  {props.quantity} x {product.currency} {product.price.toFixed(2)}
                </span>
                  </div>
                }

                {product.isPriceOnDemand &&
                  <div className="price">
                    {product.priceOnDemandText}
                  </div>
                }
              </div>
            </Link>

            {props.type == "wishlist" &&
              <div className="actions">
                <UpdateProductQuantity productId={product.productID}
                                       currentQuantity={props.quantity}
                                       type="wishlist"/>
                <AddToWishlist productId={product.productID}
                               layout="remove"
                               variant="transparent"/>
              </div>
            }

            {props.type == "cart" && clientCtx &&
              <div className="actions">
                <UpdateProductQuantity productId={product.productID}
                                       currentQuantity={props.quantity}
                                       maxQuantity={product.maxOrderQuantity}
                                       type="cart"/>
                <AddToCart productId={product.productID}
                           currency={product.currency}
                           name={product.productName}
                           price={product.price}
                           gaCId={clientCtx.gaCId}
                           layout="remove"
                           variant="transparent"/>
              </div>
            }

            {props.type == "cartWithWishlist" && clientCtx &&
              <div className="actions">
                <div className="quantity">
                  <UpdateProductQuantity productId={product.productID}
                                         currentQuantity={props.quantity}
                                         type="cart"/>
                </div>
                <AddToWishlist layout={"default"}
                               productId={product.productID}
                               variant="transparent"/>
                <AddToCart productId={product.productID}
                           currency={product.currency}
                           name={product.productName}
                           price={product.price}
                           gaCId={clientCtx.gaCId}
                           layout="remove"
                           variant="transparent"/>
              </div>
            }

            {props.type == "wishlistWithCart" && clientCtx &&
              <div className="actions">
                <div className="quantity">
                  <UpdateProductQuantity wishlistId={props.wishlistId}
                                         productId={product.productID}
                                         currentQuantity={props.quantity}
                                         type="wishlist"/>
                </div>
                <AddToCart layout={"default"}
                           icon={true}
                           productId={product.productID}
                           currency={product.currency}
                           name={product.productName}
                           price={product.price}
                           gaCId={clientCtx.gaCId}
                           variant="transparent"/>
                <AddToWishlist productId={product.productID}
                               wishlistId={props.wishlistId}
                               layout="remove"
                               variant="transparent"/>
              </div>
            }
          </div>

          {showSetupOptions && productSetupOptionsQuery.data && productSetupOptionsQuery.data.length > 0 && (
              <ProductSetupOption
                  options={productSetupOptionsQuery.data}
                  productId={product.productID}
                  quantity={quantity}
                  shoppingCart={props.shoppingCart ? props.shoppingCart : [] as ICartItem[]}
              />
          )}
        </div>
      </Entry>
    );
  } else {
    return (<></>)
  }
}

export default MiniCartProduct;
